/**
 * EncuestaPreanaliticaPanel — Panel compacto de encuesta preanalítica.
 * Combina PerfilClinicoBase + CondicionActual con opción "sin cambios".
 *
 * Requirements: M4.3.1–M4.3.4, M4.15.2
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { useApiError } from "@/hooks/use-api-error";
import {
  useAtencionDetalle,
  usePerfilClinicoPrevio,
  useRegistrarEncuesta,
  type PerfilClinicoBaseData,
  type CondicionActualData,
} from "@/hooks/use-muestras";

import { PerfilClinicoForm } from "./perfil-clinico-form";
import { CondicionActualForm } from "./condicion-actual-form";

interface EncuestaPreanaliticaPanelProps {
  atencionId: number;
  onComplete?: () => void;
}

export function EncuestaPreanaliticaPanel({
  atencionId,
  onComplete,
}: EncuestaPreanaliticaPanelProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const { data: atencion } = useAtencionDetalle(atencionId);
  const { data: perfilPrevio } = usePerfilClinicoPrevio(atencionId);

  const [perfil, setPerfil] = useState<PerfilClinicoBaseData>({
    es_diabetico: false,
    es_hipertenso: false,
    enfermedad_renal: false,
    enfermedad_tiroidea: false,
    usa_anticoagulantes: false,
    tiene_anemia: false,
    plaquetas_bajas: false,
  });

  const [condicion, setCondicion] = useState<CondicionActualData>({
    esta_en_ayunas: false,
    hora_ultima_comida: null,
    sintomas_actuales: "",
    medicamentos_recientes: "",
    motivo_examen: "",
  });

  const [sinCambios, setSinCambios] = useState(false);

  // Pre-cargar condición actual si ya fue guardada en esta atención
  useEffect(() => {
    if (atencion?.condicion_actual) {
      const ca = atencion.condicion_actual;
      setCondicion({
        esta_en_ayunas: ca.esta_en_ayunas,
        hora_ultima_comida: ca.hora_ultima_comida ?? null,
        sintomas_actuales: ca.sintomas_actuales ?? "",
        medicamentos_recientes: ca.medicamentos_recientes ?? "",
        motivo_examen: ca.motivo_examen ?? "",
      });
    }
  }, [atencion?.condicion_actual]);

  // Pre-cargar perfil clínico base del perfil más reciente de la atención
  useEffect(() => {
    if (atencion?.perfil_clinico && atencion.perfil_clinico.length > 0) {
      const p = atencion.perfil_clinico[atencion.perfil_clinico.length - 1];
      setPerfil({
        es_diabetico: p.es_diabetico,
        es_hipertenso: p.es_hipertenso,
        enfermedad_renal: p.enfermedad_renal,
        enfermedad_tiroidea: p.enfermedad_tiroidea,
        usa_anticoagulantes: p.usa_anticoagulantes,
        tiene_anemia: p.tiene_anemia,
        plaquetas_bajas: p.plaquetas_bajas,
      });
    } else if (perfilPrevio) {
      // Si no hay perfil en esta atención, pre-cargar el de la visita anterior
      setPerfil({
        es_diabetico: perfilPrevio.es_diabetico,
        es_hipertenso: perfilPrevio.es_hipertenso,
        enfermedad_renal: perfilPrevio.enfermedad_renal,
        enfermedad_tiroidea: perfilPrevio.enfermedad_tiroidea,
        usa_anticoagulantes: perfilPrevio.usa_anticoagulantes,
        tiene_anemia: perfilPrevio.tiene_anemia,
        plaquetas_bajas: perfilPrevio.plaquetas_bajas,
      });
    }
  }, [atencion?.perfil_clinico, perfilPrevio]);

  const registrarMutation = useRegistrarEncuesta(atencionId, {
    onSuccess: () => {
      toast.success(t("muestras.encuesta.guardada"));
      onComplete?.();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleGuardar = () => {
    registrarMutation.mutate({ perfil, condicion, sin_cambios: sinCambios });
  };

  // Punto 4 (Issue #87): síntomas actuales es obligatorio para continuar
  const puedeGuardar = (condicion.sintomas_actuales ?? "").trim().length > 0;

  return (
    <div className="space-y-4">
      {/* Perfil clínico base */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{t("muestras.encuesta.perfil_clinico")}</CardTitle>
            {perfilPrevio && (
              <Button
                variant={sinCambios ? "default" : "outline"}
                size="sm"
                onClick={() => setSinCambios(!sinCambios)}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                {t("muestras.encuesta.sin_cambios")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <PerfilClinicoForm value={perfil} onChange={setPerfil} disabled={sinCambios} />
        </CardContent>
      </Card>

      <Separator />

      {/* Condición actual */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("muestras.encuesta.condicion_actual")}</CardTitle>
        </CardHeader>
        <CardContent>
          <CondicionActualForm value={condicion} onChange={setCondicion} />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleGuardar} disabled={!puedeGuardar || registrarMutation.isPending}>
          {registrarMutation.isPending
            ? t("common.loading")
            : t("muestras.encuesta.guardar_continuar")}
        </Button>
      </div>
    </div>
  );
}
